import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { parseListInput } from '@/lib/community-curation/parseListInput';
import type {
  CurationNiche, CurationNicheInput, CurationRegion,
} from '@/lib/community-curation/types';

interface NicheFormDialogProps {
  open: boolean;
  niche: CurationNiche | null;
  saving: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: CurationNicheInput) => void;
}

interface FormState {
  name: string;
  region: CurationRegion;
  keywords: string;
  seedChannels: string;
  minDuration: string;
  maxDuration: string;
  lookbackHours: string;
  dailyLimit: string;
  enabled: boolean;
}

const EMPTY_FORM: FormState = {
  name: '',
  region: 'BR',
  keywords: '',
  seedChannels: '',
  minDuration: '10',
  maxDuration: '180',
  lookbackHours: '72',
  dailyLimit: '3',
  enabled: true,
};

const formFromNiche = (niche: CurationNiche | null): FormState => (niche ? {
  name: niche.name,
  region: niche.region,
  keywords: niche.keywords.join('\n'),
  seedChannels: niche.seed_channels.join('\n'),
  minDuration: String(niche.min_duration_minutes),
  maxDuration: String(niche.max_duration_minutes),
  lookbackHours: String(niche.lookback_hours),
  dailyLimit: String(niche.daily_limit),
  enabled: niche.enabled,
} : EMPTY_FORM);

// An unparseable number is sent as undefined so the backend's validation (not
// a GraphQL variable error) explains it.
const toInt = (value: string): number | undefined => {
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : undefined;
};

export function NicheFormDialog({
  open, niche, saving, onOpenChange, onSubmit,
}: NicheFormDialogProps) {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [showSourceHint, setShowSourceHint] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(formFromNiche(niche));
      setShowSourceHint(false);
    }
  }, [open, niche]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const submit = () => {
    const keywords = parseListInput(form.keywords);
    const seedChannels = parseListInput(form.seedChannels);
    if (keywords.length === 0 && seedChannels.length === 0) {
      setShowSourceHint(true);
      return;
    }
    onSubmit({
      name: form.name.trim(),
      region: form.region,
      keywords,
      seedChannels,
      minDurationMinutes: toInt(form.minDuration),
      maxDurationMinutes: toInt(form.maxDuration),
      lookbackHours: toInt(form.lookbackHours),
      dailyLimit: toInt(form.dailyLimit),
      enabled: form.enabled,
    });
  };

  const numberField = (id: string, key: keyof FormState, label: string) => (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="number"
        inputMode="numeric"
        value={form[key] as string}
        onChange={(e) => set(key, e.target.value as never)}
      />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {niche
              ? t('admin.communityCuration.form.editTitle')
              : t('admin.communityCuration.form.createTitle')}
          </DialogTitle>
          <DialogDescription>{t('admin.communityCuration.form.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="niche-name">{t('admin.communityCuration.form.name')}</Label>
            <Input
              id="niche-name"
              value={form.name}
              maxLength={80}
              onChange={(e) => set('name', e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="niche-region">{t('admin.communityCuration.form.region')}</Label>
            <Select value={form.region} onValueChange={(value) => set('region', value as CurationRegion)}>
              <SelectTrigger id="niche-region">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="BR">{t('admin.communityCuration.regions.BR')}</SelectItem>
                <SelectItem value="US">{t('admin.communityCuration.regions.US')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="niche-keywords">{t('admin.communityCuration.form.keywords')}</Label>
            <Textarea
              id="niche-keywords"
              rows={3}
              value={form.keywords}
              onChange={(e) => set('keywords', e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {t('admin.communityCuration.form.keywordsHint')}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="niche-channels">{t('admin.communityCuration.form.seedChannels')}</Label>
            <Textarea
              id="niche-channels"
              rows={3}
              value={form.seedChannels}
              onChange={(e) => set('seedChannels', e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {t('admin.communityCuration.form.seedChannelsHint')}
            </p>
          </div>

          {showSourceHint && (
            <p role="alert" className="text-sm text-destructive">
              {t('admin.communityCuration.form.needsSource')}
            </p>
          )}

          <div className="grid grid-cols-2 gap-3">
            {numberField('niche-min', 'minDuration', t('admin.communityCuration.form.minDuration'))}
            {numberField('niche-max', 'maxDuration', t('admin.communityCuration.form.maxDuration'))}
            {numberField('niche-lookback', 'lookbackHours', t('admin.communityCuration.form.lookbackHours'))}
            {numberField('niche-limit', 'dailyLimit', t('admin.communityCuration.form.dailyLimit'))}
          </div>

          <div className="flex items-center gap-2">
            <Switch
              id="niche-enabled"
              checked={form.enabled}
              onCheckedChange={(checked) => set('enabled', checked)}
            />
            <Label htmlFor="niche-enabled">{t('admin.communityCuration.form.enabled')}</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('admin.communityCuration.form.cancel')}
          </Button>
          <Button disabled={saving || !form.name.trim()} onClick={submit}>
            {t('admin.communityCuration.form.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
